import { FormikContext } from "formik";
import { useDocPreview } from "hooks/useDocPreview";
import { useToast } from "hooks/useToast";
import { useContext } from "react";
import { useDrop } from "react-dnd";
import {
  useDeleteFileFromTaskMutation,
  useUploadToFormInstanceMutation,
} from "store/apis/task";
import { SelecteFile } from "types/Models/documents";
import { DocumentField, TaskFileUpdateResponse } from "types/Models/task";
import { getFieldsFromSections } from "util/getFieldsFromSections";
import { filterServerDocuments } from "utils/filterServerDocuments";
import { FileInput, FileType } from "./FileInput/FileInput";
import { DocumentPreviewModal } from "./PreviewModal";

type FormUploadDocumentsProps = {
  field: DocumentField;
  formInstanceId: string;
  groupId: string;
  suggestedDocUpload: () => void;
  docInstanceId?: string;
  isMulti?: boolean;
};

const UPLOAD_FAILED_MESSAGE = "Failed to upload the file, please try again";
const DELETE_FAILED_MESSAGE = "Failed to remove the file, please try again";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object" && "data" in error) {
    const { data } = error;

    if (data && typeof data === "object" && "message" in data) {
      const { message } = data;

      if (typeof message === "string") {
        return message;
      }
    }
  }

  return fallback;
};

export const FormUploadDocuments = ({
  field: { documents, field_label, label, field_key, tag, required, tooltip },
  formInstanceId,
  docInstanceId,
  groupId,
  suggestedDocUpload,
  isMulti = true,
}: FormUploadDocumentsProps) => {
  const openPreview = useDocPreview();
  const { alertToast } = useToast();
  const formik = useContext(FormikContext);

  const formFieldValue = formik?.values?.[field_key];
  const currentDocuments = filterServerDocuments(
    Array.isArray(formFieldValue) ? formFieldValue : documents
  );

  const [uploadDoc, { isLoading: isUploading }] =
    useUploadToFormInstanceMutation();
  const [deleteDoc, { isLoading: isDeleting, originalArgs }] =
    useDeleteFileFromTaskMutation();

  const getDocumentsFromResponse = (response: TaskFileUpdateResponse) => {
    const sections = response?.form_data?.data?.section_group?.[0]?.sections;
    const rawDocs = getFieldsFromSections(sections).find(
      (f) => f.field_key === field_key
    )?.documents;

    return filterServerDocuments(rawDocs);
  };

  const onDrop = async (newFiles: FileType[]) => {
    let serverDocuments = currentDocuments;

    for (const newFile of newFiles) {
      const fileToSign =
        "uuid" in newFile ? { file_id: newFile.uuid } : { file: newFile };

      try {
        const response = await uploadDoc({
          groupId,
          formInstanceId,
          tag: JSON.stringify(tag || []),
          fieldKey: field_key,
          ...fileToSign,
        }).unwrap();

        serverDocuments = getDocumentsFromResponse(response);
        formik?.setFieldValue(field_key, serverDocuments);
      } catch (error) {
        formik?.setFieldValue(field_key, serverDocuments);
        alertToast({ message: getErrorMessage(error, UPLOAD_FAILED_MESSAGE) });
      }
    }
  };

  const onSuggestedDocDrop = async ({ data }: { data: string }) => {
    try {
      const doc: SelecteFile = JSON.parse(data);
      await onDrop([doc]);
      suggestedDocUpload();
    } catch (error) {
      alertToast({ message: getErrorMessage(error, UPLOAD_FAILED_MESSAGE) });
    }
  };

  const deleteFile = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index?: number
  ) => {
    e?.stopPropagation();

    const fileId =
      index === undefined ? undefined : currentDocuments[index]?.uuid;

    if (!fileId) {
      return;
    }

    try {
      const response = await deleteDoc({
        groupId,
        fileId,
        formInstanceId,
        fieldKey: field_key,
      }).unwrap();

      formik?.setFieldValue(field_key, getDocumentsFromResponse(response));
    } catch (error) {
      formik?.setFieldValue(field_key, currentDocuments);
      alertToast({ message: getErrorMessage(error, DELETE_FAILED_MESSAGE) });
    }
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "doc",
    drop: onSuggestedDocDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const ref = (node: HTMLDivElement | null) => {
    drop(node);
  };

  const handleFileClick = ({ uuid }: { uuid: string }) => openPreview(uuid);

  return (
    <div ref={ref}>
      <FileInput
        label={field_label || label}
        onDrop={onDrop}
        multiple={isMulti}
        files={currentDocuments}
        file={currentDocuments[0] || null}
        onFileClick={handleFileClick}
        onDelete={deleteFile}
        fileTag={field_key}
        isUploading={isUploading}
        isDeleting={isDeleting}
        fileDeletingUuid={originalArgs?.fileId}
        isDragAccept={isOver}
        withInkleDocs
        withForm
        name={field_key}
        required={required}
        tooltipText={tooltip}
      />
      <DocumentPreviewModal />
    </div>
  );
};
